// Staff enter a text, email, or call for the member who sent it. The member
// becomes the reporter and follows it in Your requests.
import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { ticketsAPI } from '../api/client';
import type { MemberEmail, TicketDetail, TicketPerson } from '../types';
import MemberEmailPreviewModal from './MemberEmailPreviewModal';
import TicketPersonPicker from './TicketPersonPicker';
import { ScreenshotPicker } from './TicketScreenshots';
import { pasteScreenshots } from '../lib/screenshots';

const KINDS = [
  { id: 'problem', label: 'Problem' },
  { id: 'idea', label: 'Idea' },
  { id: 'question', label: 'Question' },
] as const;

const VIA = [
  { id: 'text', label: 'Text' },
  { id: 'email', label: 'Email' },
  { id: 'call', label: 'Call' },
  { id: 'in_person', label: 'In person' },
  { id: 'other', label: 'Other' },
] as const;

interface LogForSomeoneModalProps {
  onClose: () => void;
  onLogged: (ticket: TicketDetail) => void;
}

export default function LogForSomeoneModal({ onClose, onLogged }: LogForSomeoneModalProps) {
  const [member, setMember] = useState<TicketPerson | null>(null);
  const [kind, setKind] = useState<(typeof KINDS)[number]['id']>('problem');
  const [via, setVia] = useState<(typeof VIA)[number]['id']>('text');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ label: string; emails: MemberEmail[] } | null>(null);

  const errorText = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { error?: string } } }).response?.data?.error || fallback;

  const review = async () => {
    if (!member || (!message.trim() && files.length === 0)) return;
    setSaving(true);
    try {
      const result = await ticketsAPI.logPreview({ userId: member.id, kind, message: message.trim(), via });
      setPreview({ label: result.status_label, emails: result.emails });
    } catch (err) {
      toast.error(errorText(err, 'Could not preview the email.'));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (notify: boolean) => {
    if (!member) return;
    setSaving(true);
    try {
      const ticket = await ticketsAPI.logForSomeone({
        userId: member.id, kind, message: message.trim(), via, screenshots: files, notify,
      });
      toast.success(notify ? `Logged and emailed ${member.name}.` : `Logged for ${member.name}. No email sent.`);
      setPreview(null);
      onLogged(ticket);
    } catch (err) {
      toast.error(errorText(err, 'Could not log that request.'));
    } finally {
      setSaving(false);
    }
  };

  if (preview) {
    return (
      <MemberEmailPreviewModal
        title={`Log for ${member?.name || 'member'}: ${preview.label}`}
        emails={preview.emails}
        busy={saving}
        sendLabel="Log it and send this email"
        quietLabel="Log it without emailing"
        quietNote="Either way, it shows in their Your requests and they get the team's replies."
        onSend={() => void submit(true)}
        onQuiet={() => void submit(false)}
        onCancel={() => setPreview(null)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy">Log for someone</h2>
          <button type="button" onClick={onClose} className="p-1.5 pointer-coarse:p-2.5 pointer-coarse:-m-1 rounded-lg hover:bg-gray-100 transition" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">
            Paste what a member texted or emailed you. They become the reporter, see it under
            Your requests, and get every reply.
          </p>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Who sent it</p>
            <TicketPersonPicker value={member} onChange={setMember} placeholder="Search members by name or email" />
          </div>
          <div className="flex flex-wrap gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Kind</p>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {KINDS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setKind(option.id)}
                    className={`px-3 py-1.5 text-xs font-semibold transition ${
                      kind === option.id ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">How they sent it</p>
              <select
                value={via}
                onChange={(event) => setVia(event.target.value as typeof via)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
              >
                {VIA.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            onPaste={(event) => pasteScreenshots(event, files, setFiles)}
            rows={6}
            placeholder="Their words, as sent"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
          />
          <ScreenshotPicker files={files} onChange={setFiles} disabled={saving} label="Add screenshots (optional)" />
          <button
            type="button"
            disabled={saving || !member || (!message.trim() && files.length === 0)}
            onClick={() => void review()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Review and log it
          </button>
        </div>
      </div>
    </div>
  );
}
